import { fetchAuthSession } from 'aws-amplify/auth'
import { post, put, get } from 'aws-amplify/api'
import { ApiErrorService } from './apiErrorService'
import { useAuthStore } from '../stores/auth'
export interface ApiRequestOptions {
  path: string
  body?: any
  method?: 'GET' | 'POST' | 'PUT'
  apiName?: string
}
export interface ApiResponse {
  success: boolean
  data?: any
  statusCode: number
}
export async function makeApiRequest(
  options: ApiRequestOptions,
  message?: any
): Promise<ApiResponse> {
  const suppressErrors = () => useAuthStore().loggingOut
  try {
    const authToken = (await fetchAuthSession()).tokens?.idToken?.toString()
    if (!authToken) {
      if (message && !suppressErrors()) message.error('Authentication required')
      return { success: false, statusCode: 401 }
    }
    const method = options.method || 'POST'
    const apiName = options.apiName || 'apinotefieuser'
    let restOperation
    if (method === 'GET') {
      restOperation = get({
        apiName: apiName,
        path: options.path,
        options: {
          headers: {
            Authorization: authToken
          }
        }
      })
    } else if (method === 'PUT') {
      restOperation = put({
        apiName: apiName,
        path: options.path,
        options: {
          headers: {
            Authorization: authToken
          },
          body: options.body
        }
      })
    } else {
      restOperation = post({
        apiName: apiName,
        path: options.path,
        options: {
          headers: {
            Authorization: authToken
          },
          body: options.body
        }
      })
    }
    const response = await restOperation.response
    const statusCode =
      response.statusCode ??
      ('status' in response
        ? (
            response as {
              status?: number
            }
          ).status
        : undefined) ??
      200
    const responseText = await response.body.text()
    if (!responseText) {
      if (method === 'GET') {
        return { success: true, statusCode, data: null }
      } else {
        if (message && !suppressErrors()) message.warning('Empty response from server')
        return { success: false, statusCode }
      }
    }
    let responseData
    try {
      responseData = JSON.parse(responseText)
    } catch (parseError) {
      if (message && !suppressErrors()) message.error('Invalid response from server')
      return { success: false, statusCode, data: { message: responseText } }
    }
    if (statusCode !== 200) {
      if (message) {
        ApiErrorService.handleApiResponse({ statusCode, data: responseData }, message)
      }
      return { success: false, statusCode, data: responseData }
    }
    return { success: true, statusCode, data: responseData }
  } catch (error: any) {
    if (error.response) {
      try {
        const errorStatusCode = error.response.statusCode || error.response.status || 400
        const errorBody = error.response.body
        let errorData
        if (typeof errorBody === 'string') {
          try {
            errorData = JSON.parse(errorBody)
          } catch {
            errorData = { description: errorBody }
          }
        } else {
          errorData = errorBody || {}
        }
        if (message) {
          ApiErrorService.handleApiResponse(
            { statusCode: errorStatusCode, data: errorData },
            message
          )
        }
        return { success: false, statusCode: errorStatusCode, data: errorData }
      } catch {}
    }
    if (message) {
      ApiErrorService.handleApiException(error, message)
    }
    return { success: false, statusCode: 0 }
  }
}
export function isSuccessResponse(responseData: any): boolean {
  if (!responseData) return false
  if (responseData.message === 'ok') {
    return true
  }
  if (responseData.body) {
    try {
      const body =
        typeof responseData.body === 'string' ? JSON.parse(responseData.body) : responseData.body
      return body.message === 'ok'
    } catch {
      return false
    }
  }
  return false
}
export function parseResponseBody(responseData: any): any {
  if (!responseData) return {}
  if (responseData.body) {
    try {
      return typeof responseData.body === 'string'
        ? JSON.parse(responseData.body)
        : responseData.body
    } catch {
      return {}
    }
  }
  return responseData
}
